import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { PageHeader, Card, FormGroup, Input, Button } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language } from "imports/Utils";
import ExternalMasterdataAlert, { ExternalMasterdataWarningIcon, MasterdataEntity } from "components/masterdata/ExternalMasterdataAlert";

import { fleetSchema, FleetSchemaInput, FleetSchemaOutput } from "./utils/fleet.schema";

import { SaveFleetDocument, SaveFleetMutation } from "__generated__/view/fleets/graphql/fleet.mutations.generated";
import { GetFleetDocument, GetFleetQuery } from "__generated__/view/fleets/graphql/fleet.queries.generated";

const FleetCreateEdit = () => {

	const { id } = useParams();
	const navigate = useNavigate();
	const [ externalId, setExternalId ] = useState<string | null>(null);

	const { control, handleSubmit, reset } = useForm<FleetSchemaInput, unknown, FleetSchemaOutput>({
		resolver: zodResolver(fleetSchema),
		defaultValues: {
			id: null,
			description: "",
		},
	});

	const [ saveFleet, { loading } ] = useMutation<SaveFleetMutation>(SaveFleetDocument);
	const [ getFleet ] = useLazyQuery<GetFleetQuery>(GetFleetDocument);

	const save = (data: FleetSchemaOutput) => {
		saveFleet({
			variables: {
				input: data,
			},
		}).then(({ data }) => {
			navigate(`/view/fleets/${data?.saveFleet.id}/details`);
		});
	};

	useEffect(() => {
		if (id) {
			getFleet({
				variables: {
					id: Number(id),
				},
			}) .then(({ data }) => {
				setExternalId(data?.fleet?.externalId ?? null);
				reset({
					id: data?.fleet?.id ?? null,
					description: data?.fleet?.description ?? "",
				});
			});
		}
	}, []);

	return (
		<React.Fragment>
			<form onSubmit={(event) => void handleSubmit(save)(event)}>
				<PageHeader>
					<PageHeader.Title>
						{Language("FLEETS_SINGLE")}
						<Helmet title={intl.formatMessage({ id: "FLEETS_SINGLE" })} />
					</PageHeader.Title>

					<PageHeader.Actions>
						<Button onClick={() => navigate(id ? `/view/fleets/${id}/details` : "/view/fleets")} variant="outlined">
							{Language("CANCEL")}
						</Button>

						<Button loading={loading} disabled={loading} type="submit">
							{Language("SAVE")}
						</Button>

						<ExternalMasterdataWarningIcon entity={MasterdataEntity.Fleet} />
					</PageHeader.Actions>
				</PageHeader>

				<ExternalMasterdataAlert entity={MasterdataEntity.Fleet} externalId={externalId} />

				<Grid spacing={3} container>
					<Grid size={{ xs: 12, lg: 6 }}>
						<Card>
							<FormGroup
								label={Language("DESCRIPTION")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<Controller
									name="description"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<Input {...field} autoFocus />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						</Card>
					</Grid>
				</Grid>
			</form>
		</React.Fragment>
	);
};

export default FleetCreateEdit;
